import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Save, X, GripVertical, Settings2,
  Building2, Calendar, Hash, Tag, MapPin, Car, User, UserPlus,
  Gauge, MapPinned, Euro, Clock, Coffee, ChevronDown, ChevronUp,
  AlertTriangle, Loader2, Palette
} from 'lucide-react';
import api from '../lib/api';

const ICON_MAP = {
  Building2, Calendar, Hash, Tag, MapPin, Car, User, UserPlus,
  Gauge, MapPinned, Euro, Clock, Coffee
};

const CATEGORY_KEYS = {
  base: 'customers.categoryBase',
  identity: 'customers.categoryIdentity',
  personnel: 'customers.categoryPersonnel',
  distance: 'customers.categoryDistance',
  pricing: 'customers.categoryPricing',
  time: 'customers.categoryTime'
};

const CATEGORY_ORDER = ['base', 'identity', 'personnel', 'distance', 'pricing', 'time'];

const COLOR_OPTIONS = [
  { value: '#9333ea', labelKey: 'purple', bg: 'bg-purple-100 text-purple-700' },
  { value: '#ca8a04', labelKey: 'yellow', bg: 'bg-yellow-100 text-yellow-700' },
  { value: '#2563eb', labelKey: 'blue', bg: 'bg-blue-100 text-blue-700' },
  { value: '#16a34a', labelKey: 'green', bg: 'bg-green-100 text-green-700' },
  { value: '#ea580c', labelKey: 'orange', bg: 'bg-orange-100 text-orange-700' },
  { value: '#dc2626', labelKey: 'red', bg: 'bg-red-100 text-red-700' },
  { value: '#0891b2', labelKey: 'cyan', bg: 'bg-cyan-100 text-cyan-700' },
  { value: '#7c3aed', labelKey: 'violet', bg: 'bg-violet-100 text-violet-700' },
  { value: '#6b7280', labelKey: 'gray', bg: 'bg-slate-100 text-slate-700' },
  { value: '#be185d', labelKey: 'pink', bg: 'bg-pink-100 text-pink-700' },
];

export default function CustomerManagement() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [calculationTypes, setCalculationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  // Drag-and-drop reorder state — `dragId` is the row currently being held,
  // `overId` highlights the drop target. We persist on drop (not on every
  // hover) to avoid bombarding the API.
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const emptyForm = {
    name: '',
    slug: '',
    color: '#6b7280',
    bg_color: 'bg-slate-100 text-slate-700',
    fields: ['firm_id', 'date', 'vehicle_plate', 'driver', 'start_time', 'end_time'],
    calculation_type: 'hours_rate',
    default_values: { start_time: '08:00', end_time: '17:00', break_time: '00:00' },
    field_options: {}
  };
  const [form, setForm] = useState(emptyForm);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get('/customers');
      if (res.data.success) setCustomers(res.data.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchFields = useCallback(async () => {
    try {
      const res = await api.get('/customers/fields');
      if (res.data.success) {
        setAvailableFields(res.data.data.fields);
        setCalculationTypes(res.data.data.calculation_types);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchFields()]);
      setLoading(false);
    };
    init();
  }, [fetchCustomers, fetchFields]);

  // Seed if no customers
  useEffect(() => {
    if (!loading && customers.length === 0) {
      const seed = async () => {
        try {
          const res = await api.post('/customers/seed');
          if (res.data.success) {
            toast.success(`${t('customers.defaultsCreated')} (${res.data.seeded})`);
            fetchCustomers();
          }
        } catch (err) {
          console.error('Seed error:', err);
        }
      };
      seed();
    }
  }, [loading, customers.length, fetchCustomers]);

  const openAdd = () => {
    setEditingCustomer(null);
    setForm(emptyForm);
    setExpandedSections({});
    setShowForm(true);
  };

  const openEdit = (cust) => {
    setEditingCustomer(cust);
    setForm({
      name: cust.name,
      slug: cust.slug,
      color: cust.color || '#6b7280',
      bg_color: cust.bg_color || 'bg-slate-100 text-slate-700',
      fields: cust.fields || [],
      calculation_type: cust.calculation_type || 'hours_rate',
      default_values: cust.default_values || {},
      field_options: cust.field_options || {}
    });
    setExpandedSections({});
    setShowForm(true);
  };

  const toggleField = (key) => {
    setForm(prev => ({
      ...prev,
      fields: prev.fields.includes(key)
        ? prev.fields.filter(f => f !== key)
        : [...prev.fields, key]
    }));
  };

  const setDefaultValue = (key, value) => {
    setForm(prev => ({
      ...prev,
      default_values: { ...prev.default_values, [key]: value }
    }));
  };

  const setColor = (colorObj) => {
    setForm(prev => ({ ...prev, color: colorObj.value, bg_color: colorObj.bg }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('customers.nameRequired'));
      return;
    }
    if (form.fields.length === 0) {
      toast.error(t('customers.atLeastOneField'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || form.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
      };
      let res;
      if (editingCustomer) {
        res = await api.put(`/customers/${editingCustomer.id}`, payload);
      } else {
        res = await api.post('/customers', payload);
      }
      if (res.data.success) {
        toast.success(editingCustomer ? t('customers.updated') : t('customers.created'));
        setShowForm(false);
        fetchCustomers();
      } else {
        toast.error(res.data.detail || t('customers.errorOccurred'));
      }
    } catch (err) {
      toast.error(t('customers.operationFailed'));
    }
    setSaving(false);
  };

  const handleDelete = async (cust) => {
    try {
      const res = await api.delete(`/customers/${cust.id}`);
      if (res.data.success) {
        toast.success(t('customers.deleted'));
        setDeleteConfirm(null);
        fetchCustomers();
      } else {
        toast.error(res.data.detail || t('customers.cannotDelete'));
      }
    } catch (err) {
      toast.error(t('customers.deleteFailed'));
    }
  };

  const toggleSection = (cat) => {
    setExpandedSections(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // ── Drag-and-drop reorder ──────────────────────────────────────────
  const handleDragStart = (e, id) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = 'move'; } catch { /* noop */ }
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  };
  const handleDragLeave = () => setOverId(null);
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const srcIdx = customers.findIndex(c => c.id === sourceId);
    const tgtIdx = customers.findIndex(c => c.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    const next = [...customers];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    setCustomers(next); // Optimistic — flip back if persist fails
    setReordering(true);
    try {
      const res = await api.post('/customers/reorder', { order: next.map(c => c.id) });
      if (!res.data?.success) throw new Error(res.data?.detail || 'Reorder failed');
      toast.success(t('customers.orderSaved') || 'Sıralama kaydedildi');
    } catch {
      toast.error(t('customers.orderFailed') || 'Sıralama kaydedilemedi');
      fetchCustomers();
    } finally {
      setReordering(false);
    }
  };

  const fieldsByCategory = CATEGORY_ORDER.map(cat => ({
    category: cat,
    label: t(CATEGORY_KEYS[cat]),
    fields: availableFields.filter(f => f.category === cat)
  })).filter(g => g.fields.length > 0);

  const calcType = calculationTypes.find(c => c.id === form.calculation_type);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="customer-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6 text-blue-600" />
            {t('customers.title')}
            {reordering && <Loader2 className="h-4 w-4 animate-spin text-blue-500" data-testid="reorder-spinner" />}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('customers.description')}
            <span className="ml-2 inline-flex items-center gap-1 text-slate-400 italic">
              <GripVertical className="h-3 w-3" /> {t('customers.dragHint') || 'Satırları sürükleyerek sıralamayı değiştirin — Route formundaki müşteri sırası da bu sıraya göre olur.'}
            </span>
          </p>
        </div>
        <Button onClick={openAdd} data-testid="add-customer-btn">
          <Plus className="h-4 w-4 mr-1" /> {t('customers.addButton')}
        </Button>
      </div>

      {/* Customer List */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead className="w-12">#</TableHead>
                <TableHead>{t('routes.customer')}</TableHead>
                <TableHead>{t('customers.fields')}</TableHead>
                <TableHead>{t('customers.calculation')}</TableHead>
                <TableHead>{t('common.type')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((cust, i) => (
                <TableRow
                  key={cust.id}
                  data-testid={`customer-row-${cust.slug}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, cust.id)}
                  onDragOver={(e) => handleDragOver(e, cust.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, cust.id)}
                  className={`${dragId === cust.id ? 'opacity-50' : ''} ${overId === cust.id && dragId && dragId !== cust.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''} cursor-move`}
                >
                  <TableCell className="text-slate-300 hover:text-slate-500 select-none" data-testid={`drag-handle-${cust.slug}`}>
                    <GripVertical className="h-4 w-4" />
                  </TableCell>
                  <TableCell className="text-slate-400">{i + 1}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cust.color }} />
                      <span className="font-medium">{cust.name}</span>
                      <Badge variant="outline" className="text-xs">{cust.slug}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(cust.fields || []).slice(0, 5).map(f => {
                        const field = availableFields.find(af => af.key === f);
                        return (
                          <Badge key={f} variant="secondary" className="text-xs">
                            {field?.label_key ? t(field.label_key).split(' ')[0] : (field?.label?.split(' ')[0] || f)}
                          </Badge>
                        );
                      })}
                      {(cust.fields || []).length > 5 && (
                        <Badge variant="secondary" className="text-xs">+{cust.fields.length - 5}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-600">
                      {(() => {
                        const ct = calculationTypes.find(c => c.id === cust.calculation_type);
                        return ct?.label_key ? t(ct.label_key) : (ct?.label || cust.calculation_type);
                      })()}
                    </span>
                  </TableCell>
                  <TableCell>
                    {cust.is_system ? (
                      <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">{t('customers.system')}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-200">{t('customers.custom')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(cust)} data-testid={`edit-customer-${cust.slug}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!cust.is_system && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => setDeleteConfirm(cust)} data-testid={`delete-customer-${cust.slug}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    {t('customers.noCustomers')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {t('customers.deleteTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm">{t('customers.deleteMessage', { name: deleteConfirm?.name })}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>{t('customers.cancel')}</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>{t('customers.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-blue-600" />
              {editingCustomer ? t('customers.editCustomer', { name: editingCustomer.name }) : t('customers.newCustomer')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">{t('customers.name')} *</Label>
                <Input
                  value={form.name}
                  onChange={e => {
                    const name = e.target.value;
                    setForm(prev => ({
                      ...prev,
                      name,
                      slug: editingCustomer?.is_system ? prev.slug : name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                    }));
                  }}
                  placeholder={t('customers.namePlaceholder')}
                  data-testid="customer-form-name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">{t('customers.slug')}</Label>
                <Input
                  value={form.slug}
                  onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder={t('customers.slugPlaceholder')}
                  disabled={editingCustomer?.is_system}
                  className="font-mono text-sm"
                  data-testid="customer-form-slug"
                />
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5 mb-2">
                <Palette className="h-4 w-4" /> {t('customers.color')}
              </Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map(c => (
                  <button
                    key={c.value}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c.value ? 'border-slate-900 dark:border-white scale-110' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c.value }}
                    onClick={() => setColor(c)}
                    title={c.labelKey}
                    data-testid={`color-${c.labelKey}`}
                  />
                ))}
              </div>
            </div>

            {/* Calculation Type */}
            <div>
              <Label className="text-sm font-medium mb-2 block">{t('customers.calculationMethod')} *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {calculationTypes.map(ct => (
                  <div
                    key={ct.id}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      form.calculation_type === ct.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                    onClick={() => setForm(prev => ({ ...prev, calculation_type: ct.id }))}
                    data-testid={`calc-type-${ct.id}`}
                  >
                    <div className="font-medium text-sm">{ct.label_key ? t(ct.label_key) : ct.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono">{ct.formula}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{t('customers.example')}: {ct.example}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Field Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                {t('customers.formFields')} * <span className="text-xs text-slate-400 font-normal ml-1">({form.fields.length} {t('customers.fieldsSelected')})</span>
              </Label>
              <div className="space-y-2">
                {fieldsByCategory.map(group => {
                  const isOpen = expandedSections[group.category] !== false;
                  const selectedInGroup = group.fields.filter(f => form.fields.includes(f.key)).length;
                  return (
                    <div key={group.category} className="border rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        onClick={() => toggleSection(group.category)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{group.label}</span>
                          {selectedInGroup > 0 && (
                            <Badge className="h-5 text-xs">{selectedInGroup}/{group.fields.length}</Badge>
                          )}
                        </div>
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {isOpen && (
                        <div className="p-2 space-y-1">
                          {group.fields.map(field => {
                            const IconComp = ICON_MAP[field.icon] || Hash;
                            const isSelected = form.fields.includes(field.key);
                            return (
                              <div key={field.key} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleField(field.key)}
                                  data-testid={`field-${field.key}`}
                                />
                                <IconComp className="h-4 w-4 text-slate-400 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm">{field.label_key ? t(field.label_key) : field.label}</span>
                                  <span className="text-xs text-slate-400 ml-1">({field.type})</span>
                                </div>
                                {isSelected && (field.type === 'currency' || field.type === 'number' || field.type === 'time') && (
                                  <div className="shrink-0">
                                    <Input
                                      className="h-7 w-24 text-xs"
                                      placeholder={t('customers.defaultValue')}
                                      value={form.default_values[field.key] || ''}
                                      onChange={e => setDefaultValue(field.key, e.target.value)}
                                      onClick={e => e.stopPropagation()}
                                      data-testid={`default-${field.key}`}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                {t('customers.preview')}
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: form.color }} />
                <Badge className={form.bg_color}>{form.name || '...'}</Badge>
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {form.fields.map(fKey => {
                  const field = availableFields.find(af => af.key === fKey);
                  if (!field) return null;
                  return (
                    <div key={fKey} className="flex items-center gap-1.5 text-xs text-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 rounded">
                      <span>{field.label_key ? t(field.label_key) : field.label}</span>
                      {form.default_values[fKey] && (
                        <span className="text-blue-500 ml-auto">={form.default_values[fKey]}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {calcType && (
                <div className="mt-2 text-xs text-slate-500">
                  {t('customers.calculation')}: <span className="font-mono text-blue-600">{calcType.formula}</span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t('customers.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="customer-form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {editingCustomer ? t('customers.update') : t('customers.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
